import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Lock, MessageSquareText, Play as PlayIcon } from "lucide-react";
import { resolvePlayers } from "@/lib/geometry";
import { HIT_TEXT, RUNNER_TEXT } from "@/lib/jobs";
import { PlayField } from "@/components/field/PlayField";
import { readyPositions } from "@/components/PlaybookView";

export const GameDayView = ({ cfg, plays }) => {
  const [state, setState] = useState({ i: 0, n: 1, primed: false });
  const go = useCallback((i) => {
    setState((s) => ({ ...s, primed: true }));
    setTimeout(() => setState((s) => ({ i, n: s.n + 1, primed: false })), 350);
  }, []);
  const count = plays.length;
  const i = Math.min(state.i, Math.max(0, count - 1));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" && i < count - 1) go(i + 1);
      if (e.key === "ArrowLeft" && i > 0) go(i - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [i, count, go]);

  if (!count) {
    return (
      <div className="card text-center !py-10 space-y-2" data-testid="gameday-empty">
        <p className="font-heading text-2xl font-bold uppercase">No game day plays yet</p>
        <p className="font-medium text-[var(--muted)]">Customize plays or assign drills, then pick them here.</p>
      </div>
    );
  }
  const play = plays[i];
  const positions = state.primed ? readyPositions(cfg) : resolvePlayers(play, play.ball, cfg);
  return (
    <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr] items-start" data-testid="gameday-view">
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="gd-counter" data-testid="gameday-counter">{i + 1} / {count}</span>
          <h2 className="font-heading text-2xl sm:text-4xl font-extrabold uppercase leading-none" data-testid="gameday-title">
            {HIT_TEXT[play.hit_type]} to {play.fielder}
          </h2>
        </div>
        <p className="font-heading text-xl sm:text-2xl font-bold uppercase text-[var(--muted)]" data-testid="gameday-situation">
          {play.outs} out{play.outs === 1 ? "" : "s"} · {RUNNER_TEXT[play.runners]}
        </p>
        <div className="field-frame">
          <PlayField cfg={cfg} runners={play.runners} positions={positions} throws={play.throws} runnerPaths={play.runner_paths}
            hit={state.primed ? null : { id: state.n, ball: play.ball, hitType: play.hit_type, foul: false }} playId={`gd-${state.n}`} />
        </div>
        <div className="flex gap-3">
          <button className="gd-btn" data-testid="gameday-prev-button" disabled={i === 0} onClick={() => go(i - 1)}><ChevronLeft size={30} /> Prev</button>
          <button className="gd-btn !flex-none !px-5" data-testid="gameday-replay-button" onClick={() => go(i)} aria-label="Replay"><PlayIcon size={28} /></button>
          <button className="gd-btn gd-btn-primary" data-testid="gameday-next-button" disabled={i === count - 1} onClick={() => go(i + 1)}>Next <ChevronRight size={30} /></button>
        </div>
      </div>
      <div className="card space-y-3">
        <p className="text-lg font-semibold leading-snug" data-testid="gameday-explanation">{play.explanation}</p>
        {play.notes.map((n) => <p key={n} className="note-callout">{n}</p>)}
        {play.coach_note && <div className="coach-callout" data-testid="gameday-coach-note"><MessageSquareText size={20} className="shrink-0" />{play.coach_note}</div>}
        {play.staff_note && <div className="staff-callout" data-testid="gameday-staff-note"><Lock size={18} className="shrink-0" /><span><b>Coaches only:</b> {play.staff_note}</span></div>}
      </div>
    </div>
  );
};
